// v2 maple — a low-forked autumn maple: a short trunk splitting into twin
// leaders, with the round crown built as three stacked colour bands (deep crimson
// below, orange in the middle, bright amber on top — fire rising through the
// canopy) plus a glowing top cap, and flattened drifts of fallen leaves at the
// foot. Variants re-spin the bands, the fork and the autumn palette
// deterministically. Ported 1:1 from age-of-mcp's v2/tree-maple.ts, using the
// library's shared `tree` (h01, lobe, pick) helpers.
import * as THREE from "three";
import { defineModel, tree, type Part } from "../../lib";

const PALETTES = [
  { low: "#9c3414", mid: "#c25a1c", hi: "#dd8628", top: "#ecaa3c" },
  { low: "#8f2e1a", mid: "#b04420", hi: "#cf6c24", top: "#e29336" },
  { low: "#a84718", mid: "#cc6e20", hi: "#e3962e", top: "#f0b948" },
] as const;

const BARK = "#54382a";
const BARK_LIGHT = "#5e4030";

function mapleParts(v: number): Part[] {
  const seed = 83 + v * 7;
  const yaw = v * 2.2;
  const pal = tree.pick(PALETTES, v);
  const parts: Part[] = [
    // short trunk forking low into twin leaders
    { geometry: new THREE.CylinderGeometry(0.09, 0.14, 0.45, 6),
      color: BARK, position: [0, 0.235, 0] },
    { geometry: new THREE.CylinderGeometry(0.05, 0.075, 0.6, 5),
      color: BARK, position: [-0.1, 0.72, 0.02], rotation: [0, 0, 0.3] },
    { geometry: new THREE.CylinderGeometry(0.045, 0.07, 0.65, 5),
      color: BARK_LIGHT, position: [0.11, 0.76, -0.03], rotation: [-0.06, 0, -0.32] },
  ];
  // bottom band — deep crimson ring
  for (let k = 0; k < 3; k++) {
    const ang = yaw + k * 2.1 + tree.h01(seed, 20 + k) * 0.5;
    const d = 0.27 + tree.h01(seed, 24 + k) * 0.07;
    parts.push(tree.lobe(seed, 28 + k,
      Math.cos(ang) * d, 1.0 + tree.h01(seed, 32 + k) * 0.05, Math.sin(ang) * d,
      0.3 + tree.h01(seed, 36 + k) * 0.03, pal.low, 0.7));
  }
  // middle band — orange ring, staggered against the bottom one
  for (let k = 0; k < 3; k++) {
    const ang = yaw + 1.05 + k * 2.1 + tree.h01(seed, 40 + k) * 0.5;
    const d = 0.2 + tree.h01(seed, 44 + k) * 0.06;
    parts.push(tree.lobe(seed, 48 + k,
      Math.cos(ang) * d, 1.22 + tree.h01(seed, 52 + k) * 0.05, Math.sin(ang) * d,
      0.27 + tree.h01(seed, 56 + k) * 0.03, k % 2 === 0 ? pal.mid : pal.hi, 0.72));
  }
  // top band — one bright amber lobe + a glowing cap
  parts.push(tree.lobe(seed, 60, 0.02, 1.46, 0, 0.27, pal.hi, 0.8));
  parts.push(tree.lobe(seed, 61, 0.05, 1.6, 0.05, 0.15, pal.top, 0.8));
  // fallen-leaf drifts at the foot
  parts.push(tree.lobe(seed, 62, 0.3, 0.018, 0.18, 0.14, pal.mid, 0.12));
  parts.push(tree.lobe(seed, 63, -0.26, 0.015, -0.2, 0.11, pal.top, 0.12));
  return parts;
}

export const treeMapleV2 = defineModel({
  id: "tree-maple-v2",
  batchId: "fable",
  name: "Tree maple (v2)",
  tags: ["scenery","tree","maple","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree maple.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.58,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => mapleParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => mapleParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => mapleParts(2) },
  ],
  animations: [],
});
